import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import Layout from '@components/Layout';
import {
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  getDocs,
  query,
  where,
  arrayUnion,
  increment,
  updateDoc,
  onSnapshot,
} from 'firebase/firestore';

import { db } from '@root/firebase';
import { toast } from 'react-hot-toast';
import LikeButton from '@components/LikeButton';
import FavoriteButton from '@components/FavoriteButton';
import '@css/custom/link-fav.css';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css'; // Import styles
import StarRating from '@components/StarRating';

function Articles() {
  const { globalUser, logout, defaultAvatarUrl } = useAuth();
  const [libraries, setLibraries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [totalFileCount, setTotalFileCount] = useState(0);
  const [recentFileCount, setRecentFileCount] = useState(0);
  const [fileViewCount, setFileViewCount] = useState(0);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [topViewed, setTopViewed] = useState([]);
  const [searchTerms, setSearchTerms] = useState([]);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isCommentsCollapsed, setIsCommentsCollapsed] = useState(false);
  const [mainContentSize, setMainContentSize] = useState(9);

  const [showMainPane, setShowMainPane] = useState(true);
  const [showViewArticle, setShowViewArticle] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [sanitizedId, setSanitizedId] = useState(null);

  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [newRating, setNewRating] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 12; // Number of results per page

  // Function to toggle collapse state
  const toggleMenu = () => {
    setMainContentSize(isCollapsed ? 12 : 9); // If collapsed, set to 12; otherwise, 9
    setIsCollapsed((prev) => !prev);
  };
  const toggleComments = () => {
    setIsCommentsCollapsed((prev) => !prev);
  };

  /*************************************************************************
   *
   * AHandle User Rating Submission
   *
   **************************************************************************/
  // ⭐
  const handleRating = async (rating) => {
    setUserRating(rating);
    setUserHasRated(true);
    console.log(`User rated: ${rating}`);
  };

  /*************************************************************************
   *
   * Handle Comment Submission
   *
   **************************************************************************/
  const handleSubmitComment = async () => {
    if (!selectedArticle || !globalUser) {
      toast.error('Article Not Found');
      return;
    }

    if (!newComment) {
      toast.error('Enter Comment');
      return;
    }

    if (!newRating) {
      toast.error('Add a Star Rating');
      return;
    }

    const commentData = {
      user_id: globalUser.uid,
      rating: newRating,
      comment: newComment,
      timestamp: new Date().toISOString(),
    };

    try {
      const articleRef = doc(db, 'articles', selectedArticle.id);
      const commentsCollectionRef = collection(articleRef, 'comments');

      await addDoc(commentsCollectionRef, commentData);

      // ✅ Update article doc in Firestore
      await updateDoc(articleRef, {
        total_reviews: increment(1),
        total_ratings: increment(newRating),
      });

      // 🧹 Clear form
      setNewComment('');
      setNewRating(0);

      // 🔁 Re-fetch comments for this article
      fetchComments(selectedArticle.id);

      // 🔁 ✅ Re-fetch this article's updated rating
      const updatedArticleSnap = await getDoc(articleRef);
      if (updatedArticleSnap.exists()) {
        const updatedArticle = {
          id: selectedArticle.id,
          ...updatedArticleSnap.data(),
        };

        // Update the selected article state
        setSelectedArticle(updatedArticle);

        // Also update it inside the paginatedResults (searchResults) to refresh rating badge in list
        setSearchResults((prev) =>
          prev.map((a) => (a.id === updatedArticle.id ? updatedArticle : a))
        );
      }
    } catch (error) {
      console.error('Error saving comment:', error);
      toast.error('Failed to submit comment');
    }
  };

  /*************************************************************************
   *
   * Fetch Comments
   *
   **************************************************************************/

  const fetchUserData = async (userId) => {
    if (!userId) return { name: 'Unknown User', avatar: defaultAvatarUrl };

    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        console.log('User Data Fetched:', userSnap.data()); // ✅ Debug log
        return userSnap.data();
      } else {
        console.warn(`User not found: ${userId}`); // ⚠️ Debug log
        return { name: 'Unknown User', avatar: defaultAvatarUrl };
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      return { name: 'Unknown User', avatar: defaultAvatarUrl };
    }
  };

  const fetchComments = async (articleId) => {
    if (!articleId) {
      console.error('No article ID provided!');
      return;
    }

    const articleRef = doc(db, 'articles', articleId);
    const commentsCollectionRef = collection(articleRef, 'comments');

    try {
      const querySnapshot = await getDocs(commentsCollectionRef);

      if (querySnapshot.empty) {
        console.warn(`No comments found for article: ${articleId}`); // ⚠️ Debug log
        setComments([]);
        return;
      }

      const loadedComments = await Promise.all(
        querySnapshot.docs.map(async (doc) => {
          const commentData = doc.data();
          const userData = await fetchUserData(commentData.user_id);

          return {
            id: doc.id,
            ...commentData,
            user_name: userData.name,
            user_avatar: userData.avatar,
          };
        })
      );

      console.log('Fetched Comments:', loadedComments); // ✅ Debug log
      setComments(loadedComments);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  useEffect(() => {
    if (selectedArticle?.id) {
      fetchComments(selectedArticle.id);
    }
  }, [selectedArticle]);

  /*************************************************************************
   *
   * Calculate Average Rating for Article
   *
   **************************************************************************/
  const getAverageRating = (article) => {
    if (!article || article.total_reviews === 0) {
      return {
        value: 'No ratings yet',
        colorClass: 'bg-secondary',
      };
    }

    const avg = article.total_ratings / article.total_reviews;
    const rounded = parseFloat(avg.toFixed(1));

    let colorClass = 'bg-secondary';
    if (rounded <= 2.5) colorClass = 'bg-danger';
    else if (rounded <= 3.5) colorClass = 'bg-warning';
    else if (rounded <= 3.9) colorClass = 'bg-info';
    else colorClass = 'bg-success';

    return { value: rounded, colorClass };
  };

  /*************************************************************************
   *
   * Add View Counts and overall counters
   *
   **************************************************************************/
  const handleArticleView = async (articleId, userId) => {
    if (!articleId || !userId) {
      console.error('Invalid article ID or user ID');
      return;
    }

    try {
      const articleRef = doc(db, 'articles', articleId);
      const counterRef = doc(db, 'articles', 'counters');

      // Fetch the article data
      const articleSnap = await getDoc(articleRef);
      let articleData = articleSnap.exists() ? articleSnap.data() : null;

      if (!articleData) {
        console.error('Article does not exist');
        return;
      }

      // Check if user has already viewed the article
      const viewedBy = articleData.viewed_by || [];
      const hasUserViewed = viewedBy.some((view) => view.user_id === userId);

      if (!hasUserViewed) {
        // Increment unique view count only if user hasn't viewed before
        await updateDoc(articleRef, {
          view_count: increment(1),
          viewed_by: arrayUnion({
            user_id: userId,
            timestamp: new Date().toISOString(),
          }),
        });
      }

      // Increment total view count (including repeated views) in articles/counters
      await updateDoc(counterRef, {
        total_views: increment(1), // Total views (not unique)
        field_count: increment(1), // Field count (every view increments)
      });

      console.log(`View recorded for article: ${articleId} by user: ${userId}`);
    } catch (error) {
      console.error('Error updating article views:', error);
    }
  };

  /*************************************************************************
   *
   * Fetch Total Article Counts
   *
   **************************************************************************/
  const fetchFileCounts = async () => {
    try {
      const articlesCollectionRef = collection(db, 'articles');
      const articlesSnapshot = await getDocs(articlesCollectionRef);

      let allArticles = [];
      if (!articlesSnapshot.empty) {
        allArticles = articlesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      }

      // Get current date and date 30 days ago
      const currentDate = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(currentDate.getDate() - 30);

      // Filter articles added in the last 30 days
      const recentArticles = allArticles.filter((article) => {
        if (article.upload_date) {
          const uploadDate = new Date(article.upload_date);
          return uploadDate >= thirtyDaysAgo && uploadDate <= currentDate;
        }
        return false;
      });

      // Sort recent articles by upload date (latest first)
      recentArticles.sort(
        (a, b) => new Date(b.upload_date) - new Date(a.upload_date)
      );

      // Get total view count for articles
      const articleCounterRef = doc(db, 'articles', 'counters');
      const articleCounterDoc = await getDoc(articleCounterRef);

      let totalViewCount = 0;
      if (articleCounterDoc.exists()) {
        const data = articleCounterDoc.data();
        totalViewCount = data.total_views || 0; // Default to 0 if undefined
      }

      // Update state with the counts
      setTotalFileCount(allArticles.length);
      setRecentFileCount(recentArticles.length);
      setFileViewCount(totalViewCount);

      // 🟢 Set search results to recent articles initially
      setSearchResults(recentArticles);
      setCurrentPage(1); // Reset pagination when loading
    } catch (error) {
      console.error('Error fetching article counts:', error);
      toast.error('Failed to fetch article statistics.');
    }
  };

  useEffect(() => {
    fetchFileCounts();
  }, []);

  useEffect(() => {
    const loadLibraries = async () => {
      const data = await fetchLibraries();
      setLibraries(data);
    };

    loadLibraries();
  }, []);

  /*************************************************************************
   *
   * Get all Article librarys
   *
   **************************************************************************/
  const fetchLibraries = async () => {
    try {
      // Fetch all libraries
      const librarySnapshot = await getDocs(collection(db, 'library'));
      const libraries = librarySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        article_count: 0, // Initialize article count
        sublibraries: [],
      }));

      console.log('Libraries fetched:', libraries.length); // ✅ Debugging log

      // Fetch all articles
      const articlesSnapshot = await getDocs(collection(db, 'articles'));

      let allArticles = [];
      if (!articlesSnapshot.empty) {
        allArticles = articlesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      }

      console.log('Articles fetched:', allArticles.length); // ✅ Debugging log

      // Map library IDs to their article counts
      const articleCountMap = {};
      allArticles.forEach((article) => {
        if (article.library_id) {
          articleCountMap[article.library_id] =
            (articleCountMap[article.library_id] || 0) + 1;
        }
      });

      console.log('Article count map:', articleCountMap); // ✅ Debugging log

      // Build a map of libraries for quick access
      const libraryMap = {};
      libraries.forEach((lib) => {
        libraryMap[lib.id] = { ...lib, sublibraries: [], article_count: 0 };
      });

      // Assign child libraries under their respective parents
      libraries.forEach((lib) => {
        if (lib.parent && libraryMap[lib.parent]) {
          libraryMap[lib.parent].sublibraries.push(libraryMap[lib.id]);
        }
      });

      // Recursive function to calculate total article count (including sublibraries)
      const calculateTotalArticleCount = (library) => {
        let totalCount = articleCountMap[library.id] || 0;
        library.sublibraries.forEach((sublib) => {
          totalCount += calculateTotalArticleCount(sublib);
        });
        library.article_count = totalCount;
        return totalCount;
      };

      // Compute the total article count for top-level libraries
      const topLevelLibraries = Object.values(libraryMap).filter(
        (lib) => !lib.parent
      );
      topLevelLibraries.forEach((lib) => calculateTotalArticleCount(lib));

      console.log('Final Library Data:', topLevelLibraries); // ✅ Debugging log

      return topLevelLibraries;
    } catch (error) {
      console.error('Error fetching libraries:', error);
      return [];
    }
  };

  const toggleAccordion = (libraryId) => {
    setActiveAccordion(activeAccordion === libraryId ? null : libraryId);
  };
  /*************************************************************************
   *
   * Top 5 Recently Viewed Files
   *
   **************************************************************************/
  const fetchRecentlyViewedFiles = async () => {
    try {
      const articlesCollectionRef = collection(db, 'articles');
      const articlesSnapshot = await getDocs(articlesCollectionRef);

      if (articlesSnapshot.empty) return;

      // Convert Firestore documents into an array with IDs
      const allArticles = articlesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter and sort by the latest "viewed_by" timestamp
      const recentlyViewedArticles = allArticles
        .filter((article) => article.viewed_by?.length > 0) // Ensure it has been viewed
        .sort((a, b) => {
          const lastViewA = new Date(
            a.viewed_by[a.viewed_by.length - 1]?.timestamp || 0
          );
          const lastViewB = new Date(
            b.viewed_by[b.viewed_by.length - 1]?.timestamp || 0
          );
          return lastViewB - lastViewA;
        })
        .slice(0, 5); // Get the top 5 most recently viewed articles

      setRecentlyViewed(recentlyViewedArticles); // Update state
    } catch (error) {
      console.error('Error fetching recently viewed articles:', error);
      toast.error('Failed to fetch recently viewed articles.');
    }
  };

  useEffect(() => {
    fetchRecentlyViewedFiles();
  }, []);

  /*************************************************************************
   *
   * Top 5 Most Viewed Files
   *
   **************************************************************************/
  const fetchTopViewedFiles = async () => {
    try {
      const articlesCollectionRef = collection(db, 'articles');
      const articlesSnapshot = await getDocs(articlesCollectionRef);

      if (articlesSnapshot.empty) return;

      // Convert Firestore documents into an array with IDs
      const allArticles = articlesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort articles by view count (highest first) and get the top 5
      const topViewedArticles = allArticles
        .filter((article) => article.view_count) // Ensure view_count exists
        .sort((a, b) => (b.view_count || 0) - (a.view_count || 0)) // Sort by highest views
        .slice(0, 5); // Get the top 5 most viewed articles

      setTopViewed(topViewedArticles); // Update state
    } catch (error) {
      console.error('Error fetching top viewed articles:', error);
      toast.error('Failed to fetch most viewed articles.');
    }
  };

  useEffect(() => {
    fetchTopViewedFiles();
  }, []);

  /*************************************************************************
   *
   * Top 5 Search words
   *
   **************************************************************************/
  useEffect(() => {
    const fetchSearchTerms = async () => {
      try {
        const docRef = doc(db, 'search', 'articles');
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          const sortedTerms = Object.entries(data)
            .sort(([, a], [, b]) => b - a) // Sort by count descending
            .slice(0, 20); // Get top 20 terms

          setSearchTerms(sortedTerms);
        }
      } catch (error) {
        console.error('Error fetching search terms:', error);
      }
    };

    fetchSearchTerms();
  }, []);

  /*************************************************************************
   *
   * Search function for Library
   *
   **************************************************************************/
  const storeSearchQuery = async (queryText) => {
    if (!queryText.trim()) return; // Ignore empty search queries

    try {
      const searchRef = doc(db, 'search', 'articles'); // Reference to Firestore document
      const searchDoc = await getDoc(searchRef);

      // Convert query to lowercase for consistency
      const queryKey = queryText.toLowerCase();

      if (searchDoc.exists()) {
        // Document exists, increment count
        const searchData = searchDoc.data();
        const newCount = (searchData[queryKey] || 0) + 1;

        await updateDoc(searchRef, {
          [queryKey]: newCount,
        });
      } else {
        // Document doesn't exist, create it with the first entry
        await setDoc(searchRef, { [queryKey]: 1 });
      }

      console.log(`Search query '${queryText}' stored successfully`);
    } catch (error) {
      console.error('Error storing search query:', error);
    }
  };

  /*************************************************************************
   *
   * Handle search when a term is clicked
   *
   **************************************************************************/
  const handleSearchTermClick = async (e, term) => {
    setSearchQuery(term);
    console.log(searchQuery);
    handleSearch(e); // Call the passed handleSearch function
  };

  /*************************************************************************
   *
   * Handle search
   *
   **************************************************************************/
  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchQuery.trim()) {
      console.log('Empty search query. Returning no results.');
      setSearchResults([]);
      return;
    }

    // Store the search query before processing the search
    await storeSearchQuery(searchQuery);

    try {
      const articlesCollectionRef = collection(db, 'articles');
      const articlesSnapshot = await getDocs(articlesCollectionRef);

      if (articlesSnapshot.empty) {
        console.log('No articles found in Firestore.');
        setSearchResults([]);
        return;
      }

      const searchQueryLower = searchQuery.toLowerCase();

      // Filter articles based on search query (title, sub_title, or tags)
      const filteredArticles = articlesSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((article) => {
          return (
            article.title?.toLowerCase().includes(searchQueryLower) ||
            article.sub_title?.toLowerCase().includes(searchQueryLower) ||
            (article.tags &&
              Array.isArray(article.tags) &&
              article.tags.some((tag) =>
                tag.toLowerCase().includes(searchQueryLower)
              ))
          );
        });

      setSearchResults(filteredArticles);
      setCurrentPage(1); // Reset pagination
    } catch (error) {
      console.error('Error searching articles:', error);
      toast.error('An error occurred while searching for articles.');
    }
  };

  /***************************************************************************
   * Searches the files collection for documents with a matching folder_id.
   *
   * @param {string} folderId - The folder_id to search for.
   * @returns {Promise<Array>} - A promise resolving to an array of matching documents.
   **************************************************************************/
  const searchFilesByFolderId = async (folderId) => {
    if (!folderId) {
      console.error('Error: folderId is undefined or empty.');
      throw new Error('Invalid folderId');
    }

    try {
      const libraryDocRef = doc(db, 'files', 'library');
      console.log('Fetching document: files/library');

      const libraryDoc = await getDoc(libraryDocRef);

      if (!libraryDoc.exists()) {
        console.log('The library document does not exist.');
        return [];
      }

      const libraryData = libraryDoc.data();
      console.log('Library data:', libraryData);

      const results = Object.entries(libraryData)
        .filter(([key, value]) => value.folder_id === folderId)
        .map(([key, value]) => ({
          id: key,
          ...value,
        }));

      if (results.length === 0) {
        console.log(
          'No matching files found in library with folder_id = ${folderId}'
        );
      }

      return results;
    } catch (error) {
      console.error('Error searching files by folder_id:', error);
      throw new Error('Failed to search files');
    }
  };

  // Pagination logic
  const indexOfLastResult = currentPage * resultsPerPage;
  const indexOfFirstResult = indexOfLastResult - resultsPerPage;
  const paginatedResults = searchResults.slice(
    indexOfFirstResult,
    indexOfLastResult
  );

  const totalPages = Math.ceil(searchResults.length / resultsPerPage);

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  /*************************************************************************
   *
   * Show Article view to the user
   *
   **************************************************************************/
  const handleFileClick = async (fileName, fileUrl, userId) => {
    if (!fileName) {
      toast.error('Invalid File name');
      return;
    }

    if (!userId) {
      toast.error('Invalid User ID');
      return;
    }

    try {
      const fileRef = doc(db, 'files', 'library'); // Reference to files/library
      const fileDoc = await getDoc(fileRef);
      const countRef = doc(db, 'files', 'counters');

      if (!fileDoc.exists()) {
        toast.error('Library document does not exist.');
        return;
      }

      const fileData = fileDoc.data();

      // Ensure the file exists inside "files/library"
      if (fileData[fileName]) {
        const viewedBy = fileData[fileName].viewed_by || []; // Get the array of users who viewed the file

        // 🟢 Check if the user has already viewed the file
        const hasUserViewed = viewedBy.some((view) => view.user_id === userId);

        if (!hasUserViewed) {
          // 🟢 Update Firestore: Increment unique view count and log user in "viewed_by"
          await updateDoc(fileRef, {
            [`${fileName}.view_count`]: increment(1), // Increment only if unique
            [`${fileName}.viewed_by`]: arrayUnion({
              user_id: userId,
              timestamp: new Date().toISOString(),
            }),
          });

          // 🟢 Ensure library counter is incremented only for new views
          await updateDoc(countRef, {
            libraryCount: increment(1), // Increment only when the user views for the first time
          });

          console.log(`View recorded for file: ${fileName} by user: ${userId}`);
        } else {
          console.log(
            `User ${userId} has already viewed ${fileName}, skipping increment.`
          );
        }

        // Open file after Firestore update
        window.open(fileUrl, '_blank');
      } else {
        toast.error('File not found in Firestore.');
      }
    } catch (error) {
      console.error('Error updating file views:', error);
      toast.error('Error updating file views.');
    }
  };

  return (
    <Layout>
      <div className='col-xl-3 col-lg-4 mb-3'>
        <button
          className='btn btn-primary btn-sm'
          type='button'
          data-bs-toggle='collapse'
          data-bs-target='#collapseWidthExample'
          aria-expanded='true'
          aria-controls='collapseWidthExample'
          onClick={toggleMenu}
        >
          {isCollapsed ? 'Hide Library' : 'Show Library'}
        </button>
      </div>
      <div className='row'>
        {/* Sidebar */}
        <div
          className='col-xl-3 col-lg-4 collapse-horizontal collapse'
          id='collapseWidthExample'
        >
          <div className='card'>
            <div className='card-header bg-transparent'>
              <h5 className='mb-0'>Smart Library</h5>
            </div>
            <div className='card-body'>
              <div className='custom-accordion' id='libraryAccordion'>
                {libraries.length > 0 ? (
                  libraries.map((library) => (
                    <div key={library.id}>
                      <a
                        className='text-body fw-medium py-1 d-flex align-items-center'
                        href={`#collapse-${library.id}`}
                        role='button'
                        aria-expanded={activeAccordion === library.id}
                        aria-controls={`collapse-${library.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleAccordion(library.id);
                        }}
                      >
                        <i
                          className={`mdi ${
                            activeAccordion === library.id
                              ? 'mdi-folder-open'
                              : 'mdi-folder'
                          } font-size-20 text-warning me-2`}
                        ></i>
                        {library.library_name}
                        <i
                          className={`mdi mdi-chevron-${
                            activeAccordion === library.id ? 'down' : 'up'
                          } accor-down-icon ms-auto`}
                        ></i>
                        <span
                          className={`badge badge-soft-${
                            library.article_count > 0 ? 'success' : 'grey'
                          } ms-2`}
                        >
                          {library.article_count}
                        </span>
                      </a>

                      {/* Sublibrary Section */}
                      <div
                        className={`collapse ${activeAccordion === library.id ? 'show' : ''}`}
                        id={`collapse-${library.id}`}
                      >
                        <div className='card border-0 shadow-none ps-2 mb-0'>
                          <ul className='list-unstyled mb-0'>
                            {library.sublibraries &&
                            library.sublibraries.length > 0 ? (
                              library.sublibraries.map((sublibrary) => (
                                <li key={sublibrary.id}>
                                  <a
                                    href='#'
                                    className='links d-flex align-items-center'
                                    onClick={(e) => {
                                      e.preventDefault();
                                      searchFilesByFolderId(sublibrary.id)
                                        .then((results) => {
                                          setSearchResults(results); // Update search results with fetched files
                                          setCurrentPage(1); // Reset pagination
                                          console.log(
                                            'Search results for folder:',
                                            sublibrary.id,
                                            results
                                          );
                                        })
                                        .catch((error) => {
                                          console.error(
                                            'Error fetching files for folder:',
                                            error
                                          );
                                          toast.error(
                                            'Failed to fetch files for the selected folder.'
                                          );
                                        });
                                    }}
                                  >
                                    <div
                                      style={{ flexGrow: 1, maxWidth: '75%' }}
                                    >
                                      <i className='mdi mdi-subdirectory-arrow-right font-size-20 text-warning me-2'></i>
                                      <small>{sublibrary.library_name}</small>
                                    </div>
                                  </a>
                                </li>
                              ))
                            ) : (
                              <li>
                                <a
                                  href='#'
                                  className='links d-flex align-items-center'
                                >
                                  <div style={{ flexGrow: 1, maxWidth: '75%' }}>
                                    <i className='mdi mdi-subdirectory-arrow-right font-size-20 text-warning me-2'></i>
                                    <small className='text-muted'>
                                      No Folders
                                    </small>
                                  </div>
                                </a>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No libraries found.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`col-lg-${mainContentSize}`}>
          {showMainPane && (
            <>
              <div className='row'>
                {/* Recent File Count */}
                <div className='col-xl-4 col-sm-6'>
                  <div className='card'>
                    <div className='card-body p-3'>
                      <div className=''>
                        <div className='d-flex align-items-center overflow-hidden'>
                          <div className='flex-shrink-0 me-3'>
                            <h1 className='fw-medium display-4 mb-0'>
                              {recentFileCount}
                            </h1>
                          </div>

                          <div className='flex-grow-1'>
                            <h5 className='font-size-15 mb-1 text-truncate'>
                              Recent Files
                            </h5>

                            <p className='text-muted font-size-11 text-truncate mb-0'>
                              Added in the last 30 days
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Total File Count */}
                <div className='col-xl-4 col-sm-6'>
                  <div className='card'>
                    <div className='card-body p-3'>
                      <div className=''>
                        <div className='d-flex align-items-center overflow-hidden'>
                          <div className='flex-shrink-0 me-3'>
                            <h1 className='fw-medium display-4 mb-0'>
                              {totalFileCount}
                            </h1>
                          </div>

                          <div className='flex-grow-1'>
                            <h5 className='font-size-15 mb-1 text-truncate'>
                              Library Files
                            </h5>

                            <p className='text-muted font-size-11 text-truncate mb-0'>
                              SMART Library Files
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Total File Views */}
                <div className='col-xl-4 col-sm-6'>
                  <div className='card'>
                    <div className='card-body p-3'>
                      <div className=''>
                        <div className='d-flex align-items-center overflow-hidden'>
                          <div className='flex-shrink-0 me-3'>
                            <h1 className='fw-medium display-4 mb-0'>
                              {fileViewCount}
                            </h1>
                          </div>

                          <div className='flex-grow-1'>
                            <h5 className='font-size-15 mb-1 text-truncate'>
                              Total Views
                            </h5>

                            <p className='text-muted font-size-11 text-truncate mb-0'>
                              Number of files been viewed
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top 5 Most Viewed Files */}
                <div className='col-xl-4 col-sm-6'>
                  <div className='card'>
                    <div className='card-body p-3'>
                      <div className=''>
                        <div className='d-flex align-items-center overflow-hidden'>
                          <div className='flex-shrink-0 me-3'>
                            <h1 className='fw-medium display-4 mb-0'>
                              {topViewed.length}
                            </h1>
                          </div>

                          <div className='flex-grow-1'>
                            <h5 className='font-size-15 mb-1 text-truncate'>
                              Most Popular Files
                            </h5>

                            <p className='text-muted font-size-11 text-truncate mb-0'>
                              Most popular library articles
                            </p>
                          </div>
                        </div>
                        {/* Table for Top Viewed Files */}
                        <div className='table-responsive'>
                          <table className='table align-middle table-nowrap table-hover mb-0'>
                            <thead className='table-light'></thead>
                            <tbody>
                              {topViewed.length > 0 ? (
                                topViewed.map((file, index) => (
                                  <tr key={file.id}>
                                    <td className='text-muted font-size-11 text-truncate mb-3'>
                                      <a
                                        href={file.file_url}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        onClick={(e) => {
                                          e.preventDefault(); // Prevent default navigation until Firestore is updated
                                          handleFileClick(
                                            file.id,
                                            file.file_url,
                                            globalUser?.uid
                                          );
                                        }}
                                      >
                                        {file.file_name}
                                      </a>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan='3'
                                    className='text-center text-muted'
                                  >
                                    No data available
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top 5 Recently Viewed Files */}
                <div className='col-xl-4 col-sm-6'>
                  <div className='card'>
                    <div className='card-body p-3'>
                      <div className=''>
                        <div className='d-flex align-items-center overflow-hidden'>
                          <div className='flex-shrink-0 me-3'>
                            <h1 className='fw-medium display-4 mb-0'>
                              {recentlyViewed.length}
                            </h1>
                          </div>

                          <div className='flex-grow-1'>
                            <h5 className='font-size-15 mb-1 text-truncate'>
                              Recently Viewed
                            </h5>

                            <p className='text-muted font-size-11 text-truncate mb-0'>
                              Most recently viewed library articles
                            </p>
                          </div>
                        </div>
                        {/* Table for Top recently Viewed Files */}
                        <div className='table-responsive'>
                          <table className='table align-middle table-nowrap table-hover mb-0'>
                            <thead className='table-light'></thead>
                            <tbody>
                              {recentlyViewed.length > 0 ? (
                                recentlyViewed.map((file, index) => (
                                  <tr key={file.id}>
                                    <td className='text-muted font-size-11 text-truncate mb-3'>
                                      <a
                                        href={file.file_url}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        onClick={(e) => {
                                          e.preventDefault(); // Prevent default navigation until Firestore is updated
                                          handleFileClick(
                                            file.id,
                                            file.file_url,
                                            globalUser?.uid
                                          );
                                        }}
                                      >
                                        {file.file_name}
                                      </a>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan='3'
                                    className='text-center text-muted'
                                  >
                                    No data available
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Search Words */}
                <div className='col-xl-4 col-sm-6'>
                  <div className='card'>
                    <div className='card-body p-3'>
                      <div className=''>
                        <div className='d-flex align-items-center overflow-hidden'>
                          <div className='flex-shrink-0 me-3'>
                            <h1 className='fw-medium display-4 mb-0'>
                              {searchTerms.length}
                            </h1>
                          </div>

                          <div className='flex-grow-1'>
                            <h5 className='font-size-15 mb-1 text-truncate'>
                              Most Searched Phrases
                            </h5>

                            <p className='text-muted font-size-11 text-truncate mb-0'>
                              Popular search terms
                            </p>
                          </div>
                        </div>
                        {/* Table for Top recently Viewed Files */}
                        <div className='table-responsive'>
                          {searchTerms.map(([searchTerm, count]) => (
                            <>
                              <a
                                href={searchTerm}
                                key={searchTerm.id}
                                onClick={(e) => {
                                  e.preventDefault(); // Prevent default navigation until Firestore is updated
                                  handleSearchTermClick(e, searchTerm);
                                }}
                              >
                                {searchTerm}{' '}
                              </a>
                              <span className='badge rounded-pill bg-success'>
                                {count}
                              </span>
                              {', '}
                            </>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search */}
              <div className='card'>
                <div className='card-header'>
                  <h5 className='card-title mb-0'>Smart Library</h5>
                  <p>
                    <strong>Tip:</strong> Search the Smart Library to find 1000s
                    of articles.
                  </p>
                </div>
                <div
                  className='card-body pt-0 pb-3'
                  style={{ position: 'relative' }}
                >
                  <form onSubmit={handleSearch}>
                    <div className='form-floating mb-3'>
                      <input
                        type='text'
                        className='form-control'
                        id='txtSearch'
                        placeholder='Search the Library'
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <label htmlFor='txtSearch'>Search the Library</label>
                    </div>
                    <button type='submit' className='btn btn-primary'>
                      Search
                    </button>
                  </form>
                </div>
              </div>

              {/* Search Results */}
              <div className='card'>
                <div className='card-header'>
                  <h4 className='card-title' id='title'>
                    Recent Files
                  </h4>
                </div>
                <div className='card-body position-relative'>
                  <div className='row mt-4' id='smartLibraryResults'>
                    {paginatedResults.length > 0 ? (
                      paginatedResults.map((result) => {
                        const sanitizedId = result.id.split('/')[0];

                        return (
                          <div key={result.id} className='col-xl-4 col-sm-6'>
                            <div className='card'>
                              {/* Article Image */}
                              <img
                                className='card-img-top img-fluid'
                                src={result.image_url}
                                alt='Card image cap'
                              ></img>

                              {/* Card Body */}
                              <div className='card-body'>
                                <h4 className='card-title'>{result.title}</h4>
                                <p className='card-text'>{result.sub_title}</p>
                                <button
                                  className='btn btn-primary btn-sm waves-effect waves-light'
                                  onClick={() => {
                                    setSelectedArticle(result); // Store the selected article
                                    setSanitizedId(sanitizedId);
                                    setShowMainPane(false);
                                    setShowViewArticle(true);
                                    handleArticleView(
                                      result.id,
                                      globalUser?.uid
                                    ); // Track the view
                                  }}
                                >
                                  Read...
                                </button>
                              </div>

                              {/* Card Footer - Date and Icons */}
                              <div className='card-footer bg-transparent border-top d-flex justify-content-between align-items-center'>
                                {/* Upload Date */}
                                <span className='font-size-12 text-muted'>
                                  {new Date(
                                    result.upload_date
                                  ).toLocaleDateString('en-US', {
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric',
                                  })}
                                </span>

                                {/* Right-Aligned Icons */}
                                <div className='d-flex gap-2'>
                                  <div
                                    className='tooltip-container'
                                    style={{
                                      display: 'inline-block',
                                      marginRight: '10px',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {(() => {
                                      const { value, colorClass } =
                                        getAverageRating(result);
                                      return (
                                        <span
                                          className={`badge ${colorClass} font-size-14`}
                                        >
                                          <i className='mdi mdi-star'></i>{' '}
                                          {value}
                                        </span>
                                      );
                                    })()}

                                    <span className='tooltip'>
                                      {result.total_reviews} Reviews
                                    </span>

                                    <span className='tooltip'>
                                      {result.total_reviews} Reviews
                                    </span>
                                  </div>
                                  <LikeButton
                                    collectionPath='articles'
                                    documentId={sanitizedId}
                                    userId={globalUser.uid}
                                  />
                                  <FavoriteButton
                                    collectionPath='articles'
                                    documentId={sanitizedId}
                                    userId={globalUser.uid}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p>No results found. Try searching for something else.</p>
                    )}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className='d-flex justify-content-between align-items-center mt-3'>
                      <button
                        className='btn btn-outline-primary'
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        className='btn btn-outline-primary'
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {showViewArticle && selectedArticle && sanitizedId && (
            <div className='card'>
              <img
                className='card-img-top img-fluid'
                src={selectedArticle.image_url}
                alt={selectedArticle.title}
              />
              <div className='card-body'>
                <div className='card-footer bg-transparent border-top d-flex justify-content-between align-items-center'>
                  <button
                    type='button'
                    className='btn btn-sm btn-outline-info waves-effect waves-light'
                    onClick={() => {
                      setShowMainPane(true);
                      setShowViewArticle(false);
                    }}
                  >
                    Back to Articles
                  </button>

                  {/* Right-Aligned Icons */}
                  <div className='d-flex gap-2'>
                    <div
                      className='tooltip-container'
                      style={{ display: 'inline-block', marginRight: '10px' }}
                    >
                      <i
                        className={`fas fa-comment-dots`}
                        style={{
                          cursor: 'pointer',
                        }}
                      ></i>
                      <span className='tooltip'>Comments</span>
                    </div>
                    <div>
                      {(() => {
                        const { value, colorClass } =
                          getAverageRating(selectedArticle);
                        return (
                          <span className={`badge ${colorClass} font-size-14`}>
                            <i className='mdi mdi-star'></i> {value}
                          </span>
                        );
                      })()}
                      <span className='tooltip'>
                        {selectedArticle.total_reviews} Reviews
                      </span>
                    </div>
                    <LikeButton
                      collectionPath='articles'
                      documentId={sanitizedId}
                      userId={globalUser.uid}
                    />
                    <FavoriteButton
                      collectionPath='articles'
                      documentId={sanitizedId}
                      userId={globalUser.uid}
                    />
                  </div>
                </div>

                <h4 className='card-title font-size-26 text-center mb-4'>
                  {selectedArticle.title}
                </h4>
                <h4 className='card-title text-muted font-size-14 text-center mb-4'>
                  {selectedArticle.sub_title}
                </h4>
                <h4 className='card-title text-muted font-size-14 text-center'>
                  {new Date(selectedArticle.upload_date).toLocaleDateString(
                    'en-US',
                    {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    }
                  )}
                </h4>
                <p
                  className='card-text'
                  dangerouslySetInnerHTML={{
                    __html: selectedArticle.article,
                  }}
                ></p>

                <hr />

                <button
                  className='btn btn-primary btn-sm mb-2'
                  type='button'
                  data-bs-toggle='collapse'
                  data-bs-target='#collapseMessages'
                  aria-expanded='true'
                  aria-controls='collapseMessages'
                  onClick={toggleComments}
                >
                  {isCommentsCollapsed ? 'Hide Comments' : 'Show Comments'}
                </button>

                <div className='collapse' id='collapseMessages'>
                  <div className='card border shadow-none card-body text-muted mb-0'>
                    <div>
                      <h5 className='font-size-14 mb-3'>Reviews:</h5>
                      <div className='text-muted mb-3'>
                        {(() => {
                          const { value, colorClass } =
                            getAverageRating(selectedArticle);
                          return (
                            <span
                              className={`badge ${colorClass} font-size-14`}
                            >
                              <i className='mdi mdi-star'></i> {value}
                            </span>
                          );
                        })()}
                        {selectedArticle.total_reviews} Reviews
                      </div>

                      <div className='border py-4 rounded'>
                        <SimpleBar style={{ maxHeight: '360px' }}>
                          {/* Reviews */}
                          {comments.length === 0 ? (
                            <div
                              className='alert alert-warning alert-dismissible alert-label-icon label-arrow fade show'
                              role='alert'
                            >
                              <i className='mdi mdi-alert-outline label-icon'></i>
                              <strong>Warning</strong> - No Review yet
                              <button
                                type='button'
                                className='btn-close'
                                data-bs-dismiss='alert'
                                aria-label='Close'
                              ></button>
                            </div>
                          ) : (
                            comments.map((comment) => (
                              <>
                                <div
                                  className='border-bottom pb-3 px-4'
                                  key={comment.id}
                                >
                                  <p className='float-sm-end text-muted font-size-13'>
                                    {new Date(
                                      comment.timestamp
                                    ).toLocaleDateString('en-US', {
                                      day: '2-digit',
                                      month: 'long',
                                      year: 'numeric',
                                    })}
                                  </p>
                                  <div className='badge bg-success mb-2'>
                                    <i className='mdi mdi-star'></i>{' '}
                                    {comment.rating} ★
                                  </div>
                                  <p className='text-muted mb-4'>
                                    {comment.comment}
                                  </p>
                                  <div className='d-flex align-items-start'>
                                    <div className='flex-grow-1 d-flex'>
                                      <img
                                        src={comment.user_avatar}
                                        className='avatar-sm rounded-circle'
                                        alt={comment.user_name}
                                      />
                                      <div className='ms-2 ps-1'>
                                        <h5 className='font-size-16 mb-0'>
                                          {comment.user_name}
                                        </h5>
                                        <p className='text-muted mb-0 mt-1'>
                                          65 Followers, 86 Reviews
                                        </p>
                                      </div>
                                    </div>

                                    {/*
                                    <div className='flex-shrink-0'>
                                      <ul className='list-inline product-review-link mb-0'>
                                        <li className='list-inline-item'>
                                          <button className='btn btn-link text-dark'>
                                            <i className='bx bx-like'></i>
                                          </button>
                                        </li>
                                        <li className='list-inline-item'>
                                          <button className='btn btn-link text-dark'>
                                            <i className='bx bx-comment-dots'></i>
                                          </button>
                                        </li>
                                      </ul>
                                    </div>
                                    */}
                                  </div>
                                </div>
                              </>
                            ))
                          )}
                        </SimpleBar>

                        {/* Add Review Form */}
                        <div className='px-4 mt-2'>
                          <div className='border rounded mt-4'>
                            <div className='px-2 py-1 bg-light d-flex'>
                              <button className='btn btn-sm btn-link text-dark'>
                                <i className='bx bx-link'></i>
                              </button>
                              <button className='btn btn-sm btn-link text-dark'>
                                <i className='bx bx-smile'></i>
                              </button>
                              <button className='btn btn-sm btn-link text-dark'>
                                <i className='bx bx-at'></i>
                              </button>
                            </div>
                            <textarea
                              rows='3'
                              className='form-control border-0 resize-none'
                              placeholder='Your Comment...'
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                            />
                          </div>

                          <div className='text-end mt-3'>
                            <StarRating totalStars={5} onRate={setNewRating} />

                            <button
                              type='button'
                              className='btn btn-success w-sm ms-2'
                              onClick={handleSubmitComment}
                            >
                              Send{' '}
                              <i className='bx bx-send ms-2 align-middle'></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Articles;
